/**
 * @file 分镜表格列显隐设置 Hook
 *
 * 设计说明：
 * - localOverrides 存在 useEpisodeStore 的 visibleColumnMaps 中，
 *   以 `${projectId}-${episodeId}` 为 key，各分集独立维护，互不干扰
 * - EpisodeHeader（面板）和 EpisodePage（表格）通过同一个 scopeKey 共享同一份覆盖层
 * - backendColumns 是服务端下发的基准值，localOverrides 是用户当前会话的覆盖
 * - 最终 visible = localOverrides[key] ?? backendColumns[key].visible
 *
 * 退出再进入时：
 * - storyboard/list 接口依赖 React Query 缓存，退出再进入不会主动重新请求
 * - 同一分集的 localOverrides 保留在 store 中，切换分集后各自独立
 */
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useMutation} from '@tanstack/react-query';
import type {ColumnDef} from '../components/StoryboardTable';
import {COLUMN_WIDTH_MAP, DEFAULT_COL_WIDTH} from '../const';
import message from '@/components/common/Message';
import {saveColumnSettings} from '@/services/storyboardService';
import type {ColumnConfigItem} from '@/services/storyboardService/interface';
import {debounce} from '@/utils';
import type {ProjectId} from '@/types';
import {useEpisodeStore} from '@/store/episode/useEpisodeStore';

/** 面板中展示的单列数据 */
export interface ColumnSetting {
    key: string;
    name: string;
    visible: boolean;
    order: number;
}

/** 将 overrides + backendColumns 合并成保存接口所需的 columns 数组 */
const toSaveColumns = (overrides: Record<string, boolean>, backendColumns: ColumnConfigItem[]) =>
    backendColumns.map((c) => ({
        key: c.key,
        visible: overrides[c.key] ?? c.visible,
    }));

export function useColumnSettings(
    episodeId: number,
    projectId: ProjectId,
    backendColumns: ColumnConfigItem[],
) {
    // 以 projectId-episodeId 为 key，各分集独立维护 override
    const scopeKey = `${projectId}-${episodeId}`;
    const visibleColumnMaps = useEpisodeStore((s) => s.visibleColumnMaps);
    const setVisibleColumnMapInStore = useEpisodeStore((s) => s.setVisibleColumnMap);

    const localOverrides = useMemo(
        () => visibleColumnMaps[scopeKey] ?? {},
        [visibleColumnMaps, scopeKey],
    );
    const setLocalOverrides = useCallback(
        (
            map:
                | Record<string, boolean>
                | ((prev: Record<string, boolean>) => Record<string, boolean>),
        ) => {
            if (typeof map === 'function') {
                const current = visibleColumnMaps[scopeKey] ?? {};
                setVisibleColumnMapInStore(scopeKey, map(current));
            } else {
                setVisibleColumnMapInStore(scopeKey, map);
            }
        },
        [scopeKey, setVisibleColumnMapInStore, visibleColumnMaps],
    );

    /**
     * 最近一次成功保存的 overrides 快照，用于接口失败时回滚。
     */
    const lastSavedOverridesRef = useRef<Record<string, boolean>>({});

    /**
     * 本次提交的列集合快照。
     * onError 只回滚"当前值与本次提交值相同"的列（用户未再修改过的列），当前值和本次提交值一样，说明用户没有在请求期间再改过这一列→ 才回滚到上次成功保存的值
     * 否则：用户已经改过了，保留用户的新值，不回滚
     * 避免覆盖用户在请求进行中继续做出的修改。
     */
    const lastSubmittedRef = useRef<ReturnType<typeof toSaveColumns>>([]);

    // ── 保存接口 ──────────────────────────────────────────────────────────
    const {mutate, isPending: isSaving} = useMutation({
        mutationFn: (cols: ReturnType<typeof toSaveColumns>) => {
            lastSubmittedRef.current = cols;
            return saveColumnSettings({projectId, episodeId, columns: cols});
        },
        onSuccess: () => {
            // 用本次实际提交的值更新快照，而非 closure 里的 localOverrides（可能包含请求期间的新操作）
            lastSavedOverridesRef.current = Object.fromEntries(
                lastSubmittedRef.current.map((c) => [c.key, c.visible]),
            );
        },
        onError: () => {
            setLocalOverrides((prev) => {
                const next = {...prev};
                lastSubmittedRef.current.forEach((c) => {
                    // 当前值和本次提交值相同 → 用户未再修改，回滚到上次成功保存的值
                    if (prev[c.key] === c.visible) {
                        next[c.key] = lastSavedOverridesRef.current[c.key] ?? c.visible;
                    }
                    // 否则用户已在请求期间修改过，保留用户的新值
                });
                return next;
            });
            void message.error('展示设置保存失败，请重试');
        },
    });

    // 防抖 500ms（前后端协商约定，不得随意缩短）
    const saveDebounced = useMemo(
        () =>
            debounce((cols: ReturnType<typeof toSaveColumns>) => {
                mutate(cols);
            }, 500),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mutate, projectId, episodeId],
    );

    // 组件卸载时取消待执行的防抖保存，避免页面已离开时仍触发请求和错误提示
    useEffect(() => () => saveDebounced.cancel(), [saveDebounced]);

    // ── toggleColumn ──────────────────────────────────────────────────────
    const toggleColumn = useCallback(
        (key: string) => {
            const currentVisible =
                localOverrides[key] ?? backendColumns.find((c) => c.key === key)?.visible ?? true;
            const next = {...localOverrides, [key]: !currentVisible};
            setLocalOverrides(next);
            saveDebounced(toSaveColumns(next, backendColumns));
        },
        [localOverrides, backendColumns, setLocalOverrides, saveDebounced],
    );

    // ── toggleAll ─────────────────────────────────────────────────────────
    const toggleAll = useCallback(
        (visible: boolean) => {
            const next = Object.fromEntries(backendColumns.map((c) => [c.key, visible]));
            setLocalOverrides(next);
            saveDebounced(toSaveColumns(next, backendColumns));
        },
        [backendColumns, setLocalOverrides, saveDebounced],
    );

    // ── 计算属性 ──────────────────────────────────────────────────────────

    /** 全量列（含隐藏），按后端 order 排序，供面板展示 */
    const settings: ColumnSetting[] = useMemo(
        () =>
            [...backendColumns]
                .sort((a, b) => a.order - b.order)
                .map((c) => ({
                    key: c.key,
                    name: c.name,
                    visible: localOverrides[c.key] ?? c.visible,
                    order: c.order,
                })),
        [backendColumns, localOverrides],
    );

    /** 过滤后传给 StoryboardTable 的列定义 */
    const visibleColumns: ColumnDef[] = useMemo(
        () =>
            backendColumns
                .filter((c) => localOverrides[c.key] ?? c.visible)
                .sort((a, b) => a.order - b.order)
                .map((c) => ({
                    key: c.key,
                    title: c.name,
                    width: COLUMN_WIDTH_MAP[c.key] ?? DEFAULT_COL_WIDTH,
                    placeholder: c.placeholder,
                    maxLength: c.maxLength,
                    tooltip: c.tooltip,
                    enableRichText: c.enableRichText,
                })),
        [backendColumns, localOverrides],
    );

    return {
        settings,
        visibleColumns,
        toggleColumn,
        toggleAll,
        isSaving,
    };
}
